import { useEffect, useRef } from "react";
import type { RefObject } from "react";
import { focusInitial, focusableWithin } from "./focus";

export interface ModalSurfaceOptions {
  /** Called on Escape and (at the caller's discretion) a backdrop click. */
  readonly onClose: () => void;
  /**
   * Whether Escape dismisses. Default `true`. Set `false` while an operation is
   * in flight or when dismissing would discard work the user cannot recover.
   */
  readonly dismissible?: boolean;
  /**
   * Whether the page behind stops scrolling. Default `true`. A side sheet that
   * only covers part of the viewport may legitimately leave the page scrollable.
   */
  readonly lockScroll?: boolean;
}

/**
 * The behaviour every modal surface in the console owes a keyboard or
 * screen-reader user, in one place: focus moves in on open, Tab and Shift+Tab
 * cycle inside, Escape closes, and focus returns to whatever opened it.
 *
 * Extracted because it kept being re-implemented per surface, and each copy
 * omitted something different — the image lightbox trapped nothing and returned
 * focus nowhere, so enlarging a diagram with the keyboard dropped the user's
 * place in the document behind it. A dialog missing one of these is not
 * slightly less polished; it is a surface a keyboard user cannot leave, or one
 * they are still typing into after it has visually gone away.
 *
 * Returns the ref to put on the panel element. Give that element
 * `tabIndex={-1}` so focus has somewhere to land when it holds no controls.
 */
export function useModalSurface({
  onClose,
  dismissible = true,
  lockScroll = true,
}: ModalSurfaceOptions): RefObject<HTMLDivElement> {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);

  // The restore target is captured once, on mount, so a re-render inside the
  // surface cannot overwrite it with an element that is about to disappear.
  useEffect(() => {
    restoreTo.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    focusInitial(panelRef.current);
    const restore = restoreTo.current;
    return () => restore?.focus();
  }, []);

  useEffect(() => {
    if (!lockScroll) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [lockScroll]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && dismissible) {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = focusableWithin(panelRef.current);
      if (focusable.length === 0) {
        // Nothing to cycle through: hold focus on the panel rather than letting
        // Tab walk out into content the surface is covering.
        event.preventDefault();
        panelRef.current?.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      const inside = panelRef.current?.contains(active) ?? false;
      if (event.shiftKey && (active === first || !inside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !inside)) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose, dismissible]);

  return panelRef;
}
